<template>
  <span class="guia-recorrido">
    <b-button size="is-small" type="is-info is-light" icon-left="question-circle" icon-pack="fas" @click="iniciar">
      {{ t("¿Cómo funciona?", "How does it work?") }}
    </b-button>

    <!-- La capa se mueve a <body> al montar: así queda encima de los modales de Buefy -->
    <div ref="capa" v-show="activa" class="guia-capa">
      <div class="guia-bloqueo"></div>
      <div v-if="hueco" class="guia-hueco" :style="estiloHueco"></div>
      <div v-if="paso" ref="globo" class="guia-globo" :class="{ centrado: !hueco }" :style="estiloGlobo" role="dialog" aria-live="polite">
        <p class="guia-contador">{{ indiceVisible }} / {{ totalVisible }}</p>
        <p class="guia-titulo">{{ paso.titulo }}</p>
        <p class="guia-texto">{{ paso.texto }}</p>
        <div class="guia-botones">
          <b-button v-if="hayAnterior" size="is-small" :disabled="moviendo" @click="mover(-1)">{{ t("Anterior", "Back") }}</b-button>
          <b-button size="is-small" type="is-primary" :loading="moviendo" @click="esUltimo ? cerrar('nunca') : mover(1)">
            {{ esUltimo ? t("Terminar", "Done") : t("Siguiente", "Next") }}
          </b-button>
        </div>
        <div v-if="!esUltimo" class="guia-pie">
          <a @click="cerrar('despues')" :title="t('Se vuelve a abrir la próxima vez que abras el backoffice', 'It opens again next time you open the backoffice')">{{ t("Ahora no", "Not now") }}</a>
          <a @click="cerrar('nunca')" :title="t('Puedes verla cuando quieras con «¿Cómo funciona?»', 'You can see it anytime with «How does it work?»')">{{ t("No volver a mostrar", "Don't show again") }}</a>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
/**
 * Guía paso a paso ("tour") para enseñar a usar una pantalla.
 *
 * Uso:
 *   <GuiaRecorrido ref="guia" id="qr-campanas" :pasos="pasosGuia" :al-terminar="cerrarModales" />
 *   - Marca los elementos con data-guia="nombre".
 *   - Cada paso: { el: "nombre" | null, titulo, texto, antes?: async () => {} }
 *       el null       → globo centrado, sin resaltar nada.
 *       antes         → prepara la pantalla (abrir o cerrar un modal) antes de mostrar el paso.
 *     Si el elemento no existe o no se ve (por el rol del usuario o porque la tabla está vacía),
 *     el paso se salta solo.
 *   - this.$refs.guia.autoIniciar() la abre sola mientras el usuario no la haya cerrado para siempre.
 *
 * Al cerrar:
 *   - "Ahora no" (o Esc): se vuelve a abrir sola en la siguiente sesión del navegador.
 *   - "No volver a mostrar" o "Terminar": ya no se abre sola. Se guarda en el usuario
 *     (API /guias/vistas) para que valga en cualquier computadora, y en este navegador como respaldo.
 *   - El botón "¿Cómo funciona?" la abre siempre.
 */
import axios from "axios";

const ESPERA_TRANSICION = 280;
const API_GUIAS = `${process.env.VUE_APP_API_URL}/guias/vistas`;

const usuario = () => {
  try {
    return localStorage.getItem("userId") || "anon";
  } catch (e) {
    return "anon";
  }
};
const claveVista = (id) => `guia-vista:${id}:${usuario()}`;
const clavePospuesta = (id) => `guia-pospuesta:${id}:${usuario()}`;

const leer = (almacen, clave) => {
  try {
    return almacen.getItem(clave) === "1";
  } catch (e) {
    return false;
  }
};
const guardar = (almacen, clave) => {
  try {
    almacen.setItem(clave, "1");
  } catch (e) {
    // Sin almacenamiento solo cuenta lo que diga la API
  }
};
const cabeceras = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Las guías vistas del usuario se piden una sola vez por carga de página (y por usuario)
let vistasServidor = { usuario: null, promesa: null };
const guiasVistasDelUsuario = () => {
  if (vistasServidor.usuario !== usuario() || !vistasServidor.promesa) {
    vistasServidor = {
      usuario: usuario(),
      promesa: axios
        .get(API_GUIAS, { headers: cabeceras() })
        .then(({ data }) => data.vistas || [])
        .catch(() => []),
    };
  }
  return vistasServidor.promesa;
};

export default {
  name: "GuiaRecorrido",
  props: {
    id: { type: String, required: true },
    pasos: { type: Array, required: true },
    // Se llama al cerrar la guía de cualquier forma, para dejar la pantalla como estaba
    alTerminar: { type: Function, default: null },
  },
  data() {
    return {
      iso: localStorage.getItem("iso") || "es",
      activa: false,
      indice: -1,
      hueco: null,
      posGlobo: { top: 0, left: 0 },
      moviendo: false,
      // Contador "2 / 7": se recalcula en cada paso porque depende de lo que hay en pantalla
      indiceVisible: 0,
      totalVisible: 0,
    };
  },
  computed: {
    paso() {
      return this.pasos[this.indice] || null;
    },
    hayAnterior() {
      return this.indiceVisible > 1;
    },
    esUltimo() {
      return this.indiceVisible === this.totalVisible;
    },
    estiloHueco() {
      const h = this.hueco;
      return { top: `${h.top}px`, left: `${h.left}px`, width: `${h.width}px`, height: `${h.height}px` };
    },
    estiloGlobo() {
      return this.hueco ? { top: `${this.posGlobo.top}px`, left: `${this.posGlobo.left}px` } : {};
    },
  },
  created() {
    this.$bus.on("change-iso", () => (this.iso = localStorage.getItem("iso")));
  },
  mounted() {
    document.body.appendChild(this.$refs.capa);
    window.addEventListener("resize", this.medir);
    window.addEventListener("scroll", this.medir, true);
    // keyup en captura: Buefy cierra sus modales con Esc en keyup y aquí lo detenemos primero
    document.addEventListener("keyup", this.tecla, true);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.medir);
    window.removeEventListener("scroll", this.medir, true);
    document.removeEventListener("keyup", this.tecla, true);
    if (this.$refs.capa && this.$refs.capa.parentNode) this.$refs.capa.parentNode.removeChild(this.$refs.capa);
  },
  methods: {
    t(es, en) {
      return this.iso === "es" ? es : en;
    },
    elemento(nombre) {
      const el = document.querySelector(`[data-guia="${nombre}"]`);
      // offsetParent null = oculto (display none) o no está en la página
      return el && (el.offsetParent || el.getClientRects().length) ? el : null;
    },
    existe(nombre) {
      return Boolean(this.elemento(nombre));
    },
    /** La abre sola salvo que el usuario la haya cerrado para siempre o pospuesto en esta sesión */
    async autoIniciar() {
      if (leer(localStorage, claveVista(this.id)) || leer(sessionStorage, clavePospuesta(this.id))) return;
      const vistas = await guiasVistasDelUsuario();
      if (vistas.includes(this.id)) {
        guardar(localStorage, claveVista(this.id));
        return;
      }
      this.iniciar();
    },
    /** Guarda en el usuario que ya no se abra sola; si la API falla, al menos queda en este navegador */
    async marcarVista() {
      guardar(localStorage, claveVista(this.id));
      try {
        await axios.put(`${API_GUIAS}/${encodeURIComponent(this.id)}`, {}, { headers: cabeceras() });
        const vistas = await guiasVistasDelUsuario();
        if (!vistas.includes(this.id)) vistas.push(this.id);
      } catch (e) {
        console.log("[guia] no se pudo guardar en el usuario", e?.response?.status || e.message);
      }
    },
    async iniciar() {
      if (this.activa || !this.pasos.length) return;
      this.activa = true;
      this.indice = -1;
      await this.mover(1);
    },
    /** Avanza (1) o retrocede (-1) al siguiente paso que se pueda mostrar */
    async mover(direccion) {
      if (this.moviendo) return;
      this.moviendo = true;
      try {
        let i = this.indice + direccion;
        while (i >= 0 && i < this.pasos.length) {
          const p = this.pasos[i];
          if (p.antes) {
            await p.antes();
            await this.$nextTick();
            await new Promise((ok) => setTimeout(ok, ESPERA_TRANSICION));
          }
          if (!p.el || this.elemento(p.el)) {
            this.indice = i;
            this.recontar();
            await this.$nextTick();
            this.enfocar();
            return;
          }
          i += direccion;
        }
        // No quedó ningún paso que mostrar en esa dirección: la recorrió completa
        if (direccion > 0) this.cerrar("nunca");
      } finally {
        this.moviendo = false;
      }
    },
    /**
     * Pasos que este usuario verá: los centrados, los que tienen elemento en pantalla y los que
     * preparan la pantalla (su elemento aparece al abrir un modal, así que se cuentan).
     */
    recontar() {
      const visibles = this.pasos.map((p, i) => ({ p, i })).filter(({ p, i }) => i === this.indice || !p.el || p.antes || this.existe(p.el));
      this.totalVisible = visibles.length;
      this.indiceVisible = visibles.findIndex(({ i }) => i === this.indice) + 1;
    },
    enfocar() {
      const el = this.paso && this.paso.el && this.elemento(this.paso.el);
      if (el) el.scrollIntoView({ block: "center", inline: "nearest" });
      this.medir();
    },
    medir() {
      if (!this.activa || !this.paso) return;
      const el = this.paso.el && this.elemento(this.paso.el);
      if (!el) {
        this.hueco = null;
        return;
      }
      const r = el.getBoundingClientRect();
      const margen = 6;
      this.hueco = { top: r.top - margen, left: r.left - margen, width: r.width + margen * 2, height: r.height + margen * 2 };
      this.$nextTick(() => {
        const globo = this.$refs.globo;
        if (!globo || !this.hueco) return;
        const g = globo.getBoundingClientRect();
        const h = this.hueco;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const sep = 12;
        // Abajo si cabe; si no, arriba; si tampoco, a un lado del centro de la pantalla
        let top = h.top + h.height + sep;
        if (top + g.height > vh - 8) top = h.top - g.height - sep;
        if (top < 8) top = Math.max(8, Math.min(vh - g.height - 8, h.top + h.height / 2 - g.height / 2));
        const left = Math.max(8, Math.min(vw - g.width - 8, h.left + h.width / 2 - g.width / 2));
        this.posGlobo = { top, left };
      });
    },
    tecla(e) {
      if (!this.activa) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        this.cerrar("despues");
      } else if (e.key === "ArrowRight" && !this.esUltimo) this.mover(1);
      else if (e.key === "ArrowLeft" && this.hayAnterior) this.mover(-1);
    },
    /**
     * @param {"despues"|"nunca"} modo - "despues": vuelve a salir en la siguiente sesión;
     *   "nunca": ya no se abre sola (terminó la guía o pidió no volver a verla)
     */
    async cerrar(modo) {
      this.activa = false;
      this.indice = -1;
      this.hueco = null;
      if (modo === "nunca") this.marcarVista();
      else guardar(sessionStorage, clavePospuesta(this.id));
      if (this.alTerminar) await this.alTerminar();
    },
  },
};
</script>

<style>
/* Sin scoped: la capa vive en <body>, fuera del componente */

/* Título de la pantalla con el botón "¿Cómo funciona?" al lado */
.titulo-con-guia {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-bottom: 0.6rem;
}
.titulo-con-guia .title {
  margin-bottom: 0;
}
.guia-capa {
  position: fixed;
  inset: 0;
  z-index: 1000;
}
.guia-bloqueo {
  position: absolute;
  inset: 0;
}
.guia-hueco {
  position: fixed;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(10, 10, 10, 0.55);
  outline: 2px solid #3e8ed0;
  pointer-events: none;
  transition: all 0.2s ease;
}
.guia-globo {
  position: fixed;
  width: 340px;
  max-width: calc(100vw - 16px);
  background: #fff;
  color: #363636;
  border-radius: 10px;
  padding: 14px 16px 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
  transition: top 0.2s ease, left 0.2s ease;
}
.guia-globo.centrado {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 0 9999px rgba(10, 10, 10, 0.55), 0 8px 28px rgba(0, 0, 0, 0.25);
}
.guia-contador {
  font-size: 0.72rem;
  color: #7a7a7a;
  margin-bottom: 2px;
}
.guia-titulo {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 6px;
}
.guia-texto {
  font-size: 0.88rem;
  line-height: 1.45;
  white-space: pre-line;
}
.guia-botones {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 12px;
  gap: 6px;
}
.guia-pie {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #ededed;
  font-size: 0.78rem;
}
.guia-pie a {
  color: #7a7a7a;
}
.guia-pie a:hover {
  color: #363636;
  text-decoration: underline;
}
</style>
